import { Box, Button, Input, Popup, Table, TableRow, TextArea, TopBar } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SchambeckUser,
  TimeConfirmation,
  TimeTracking,
  YearlyTimeOverview,
  checkForValidConfirmation,
  createNewTimeTracking,
  deleteTimeTracking,
  downloadFile,
  generateEmptyTimeConfirmation,
  generateNotification,
  getTimeSheetForMonth,
  loadSchambeckUserById,
  updateSchambeckUser,
  updateTimeTracking,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { Right } from 'shared/src/userRole/UserRole.types';
import { NotAllowed } from '../../components/NotAllowed';
import { useUser } from '../../components/UserContext';
import { GenerateMonthlyOverviewBoxes } from '../../components/timeTrackingOverviewBoxes/TimeTrackingOverviewBoxes';
import { addTimeTrackingIndexedDB } from '../../utils/indexedDB/db.utils';
import {
  calculateBreakTime,
  convertTimeTrackingsIntoTableEntries,
  endTimeTracking,
  generateEmptyTimeTracking,
  getCorrectDisplayText,
  getTimeTrackingFromIndexedOrMongoDB,
  getTotalWorkingTimeForMonths,
} from '../../utils/timeTracking/TimeTracking.utils';
import {
  getUserName,
  isResponsibleHREmployee,
  isUserAllowedToDo,
} from '../../utils/user/User.utils';
import dayjs from 'shared/src/tools/Dayjs';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

const TimeKeepingPage: React.FC = () => {
  const { axios, user } = useUser();
  const [activeTracking, setActiveTracking] = useState<TimeTracking>();
  const [showWarningPopup, toggleShowWarningPopup] = useState<boolean>(false);
  const [showConfirmationPopup, toggleShowConfirmationPopup] = useState<boolean>(false);
  const [showInvalidationPopup, toggleShowInvalidationPopup] = useState<boolean>(false);
  const [newConfirmation, setNewConfirmation] = useState<TimeConfirmation>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const location = useLocation<{
    autostart?: boolean;
    userId?: string;
    month: number;
    year: number;
  }>();
  const [selectedUserId] = useState<string>(location.state?.userId || user.id);
  const [selectedUser, setSelectedUser] = useState<SchambeckUser>();
  const dateNow: Date = new Date();
  //Is updated inside boxesComponent
  const [selectedMonthAndYear, setSelectedMonthAndYear] = useState<[number, number]>([
    dateNow.getMonth(),
    dateNow.getFullYear(),
  ]);

  const [trackingToEdit, setTrackingToEdit] = useState<TimeTracking>();
  const [trackingToDelete, setTrackingToDelete] = useState<string>();

  const { data: timeTrackings, mutate } = useData('TIMETRACKING_ALL_USER_YEAR', {
    params: { userId: selectedUserId, year: selectedMonthAndYear[1] },
    config: { fallbackData: [] },
  });

  const { data: loadedOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  //UseEffect to load a simpleUser by id
  useEffect(() => {
    if (!selectedUserId) return;
    loadSchambeckUserById(selectedUserId, axios).then(setSelectedUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  /**
   * Checks if the selected month is confirmed and closed
   */
  const selectedMonthIsValid: boolean = useMemo((): boolean => {
    return (
      !!selectedUser &&
      checkForValidConfirmation(selectedMonthAndYear[0] + 1, selectedMonthAndYear[1], selectedUser)
    );
  }, [selectedMonthAndYear, selectedUser]);

  /**
   * UseEffect hook to check for existing timeTracking on indexedDB and in mongoDB, if nothing is found in indexedDB
   * With the loaded tracking, the break and workTime until now is calculated
   * Also checks if the user was redirected from the NFC page and takes action according to it
   */
  useEffect(() => {
    if (activeTracking || !user || !isUserAllowedToDo(user.right, Right.TIME_CREATE)) return;
    // Helper to avoid duplicates because of the React render cycle
    if (!isInitialRender.current) return;
    if (isInitialRender.current) {
      if (selectedUserId === user.id) {
        getTimeTrackingFromIndexedOrMongoDB(axios, selectedUserId).then((loadedTracking) => {
          //no loaded tracking and autostart => start new TimeTracking
          if (!loadedTracking && location.state?.autostart) handleStartTracking();
          //no loaded tracking and no autostart  => do nothing
          if (!loadedTracking) return;
          // loaded tracking and autostart => end Tracking
          if (loadedTracking && location.state?.autostart)
            !loadedTracking.endTime &&
              handleEndTracking({
                ...loadedTracking,
                breakTime: calculateBreakTime(loadedTracking.startTime),
              });
          //loaded tracking and no autostart => setActiveTracking
          setActiveTracking({
            ...loadedTracking,
            breakTime: calculateBreakTime(loadedTracking.startTime),
          });
        });
      }
      isInitialRender.current = false;
      // needed so a reload does not trigger another autostart
      history.replaceState({ autostart: false }, '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTracking]);

  /**
   * Util method to create a confirmation for the times of the active month or invalidate it
   * @param invalidate if true existing confirmation is invalidated
   */
  const handleAddConfirmation = useCallback(
    (invalidate?: boolean): void => {
      if (!selectedUser) return;
      const updatedUser: SchambeckUser = { ...selectedUser };
      const yearlyTimes: YearlyTimeOverview | undefined =
        updatedUser.contractInformation.yearlyTimes?.get(selectedMonthAndYear[1]);
      //invalidate existing confirmation
      if (invalidate && yearlyTimes) {
        updatedUser.contractInformation.yearlyTimes?.set(selectedMonthAndYear[1], {
          ...yearlyTimes,
          confirmations: yearlyTimes?.confirmations.map((entry) =>
            entry.month === selectedMonthAndYear[0] + 1
              ? {
                  ...entry,
                  invalid: true,
                  invalidatedAt: new Date(),
                  invalidatedFrom: user.id,
                }
              : entry,
          ),
        });
        //Add new confirmation to existing yearlyTimes
      } else if (yearlyTimes && newConfirmation) {
        updatedUser.contractInformation.yearlyTimes?.set(selectedMonthAndYear[1], {
          ...yearlyTimes,
          confirmations: [...yearlyTimes.confirmations, newConfirmation],
        });
        //create new yearlyTimes if they are not created yet
      } else if (newConfirmation) {
        updatedUser.contractInformation.yearlyTimes?.set(selectedMonthAndYear[1], {
          availableVacationDays: new Map<number, number>(),
          confirmations: [newConfirmation],
          overTimeSaldos: [],
          totalIst: 0,
          totalSoll: 0,
          totalSick: 0,
        });
      }

      updateSchambeckUser(updatedUser, axios).then(setSelectedUser);
      toggleShowConfirmationPopup(false);
      toggleShowInvalidationPopup(false);
    },
    [axios, newConfirmation, selectedMonthAndYear, selectedUser, user.id],
  );

  /**
   * Displays the buttons to confirm or invalidate the monthlyTimes based on the user rights
   */
  const confirmationButtons: React.JSX.Element = useMemo((): React.JSX.Element => {
    if (!selectedUser) return <></>;
    if (
      (isUserAllowedToDo(user.right, Right.TIME_CONFIRM) ||
        isResponsibleHREmployee(user, selectedUser, loadedOffices)) &&
      !selectedMonthIsValid
    )
      return (
        <Button
          onClick={() => {
            setNewConfirmation(
              generateEmptyTimeConfirmation(user.id, {
                month: selectedMonthAndYear[0] + 1,
              }),
            );
            toggleShowConfirmationPopup(true);
          }}
          value={t('pages.timekeeping.confirm')}
          maxWidth={300}
        />
      );
    else if (isResponsibleHREmployee(user, selectedUser, loadedOffices) && selectedMonthIsValid)
      return (
        <Button
          value={t('pages.timekeeping.invalidate')}
          maxWidth={300}
          onClick={() => {
            handleAddConfirmation(true);
          }}
        />
      );
    return <></>;
  }, [handleAddConfirmation, loadedOffices, selectedMonthIsValid, selectedUser, t, user]);

  //Holds the tableRows for timeTrackings
  const rows: TableRow[] = useMemo((): TableRow[] => {
    return convertTimeTrackingsIntoTableEntries(
      user,
      timeTrackings.filter((timeTracking) => {
        const startDate: Date = timeTracking.startDate;
        return (
          startDate.getMonth() === selectedMonthAndYear[0] &&
          startDate.getFullYear() === selectedMonthAndYear[1]
        );
      }),
      setTrackingToEdit,
      isUserAllowedToDo(user.right, Right.TIME_EDIT) ? setTrackingToDelete : undefined,
      selectedMonthIsValid,
    );
  }, [user, timeTrackings, selectedMonthIsValid, selectedMonthAndYear]);

  //Hook to update the totalWorkTIme if a timeTracking gets edited everytime the start, end or breakTime gets changed
  useEffect(() => {
    if (!trackingToEdit) return;
    const newTotalWorkTime: number =
      trackingToEdit.endTime -
      trackingToEdit.startTime -
      trackingToEdit.breakTime +
      trackingToEdit.drivingTimeArrival +
      trackingToEdit.drivingTimeDeparture;
    setTrackingToEdit({ ...trackingToEdit, totalWorkTime: newTotalWorkTime });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingToEdit?.startTime, trackingToEdit?.endTime, trackingToEdit?.breakTime]);

  /**
   * Helper method to end timeTracking and save updated objects on MongoDB and indexedDB
   * @returns void
   */
  const handleEndTracking = async (trackingToEnd: TimeTracking): Promise<void> => {
    if (!activeTracking) return;
    endTimeTracking(user.id, trackingToEnd, axios).then((updatedTracking) => {
      if (updatedTracking) setActiveTracking(updatedTracking);
    });
  };

  /**
   * Helper method to start timeTracking
   */
  const handleStartTracking = (): void => {
    createNewTimeTracking(axios, generateEmptyTimeTracking(user.id)).then((createdTracking) => {
      if (createdTracking) {
        setActiveTracking(createdTracking);
        addTimeTrackingIndexedDB(createdTracking);
      }
    });
  };
  /**
   * Helper method to handle click on stop while timeTracking is active
   * Checks if the breaktime is correct and opens comfirmation popup if not
   * @returns void
   */
  const handleStopTracking = (): void => {
    if (!activeTracking) return;
    const expectedBreakTime: number = calculateBreakTime(activeTracking.startTime);
    expectedBreakTime > activeTracking.breakTime
      ? toggleShowWarningPopup(true)
      : handleEndTracking(activeTracking);
  };
  /**
   * Helper method to create a timeCard and start the download
   * @param evt FormEvent
   */
  const handleDownloadTimeSheet = async (): Promise<void> => {
    const timeCard: Blob | undefined = await getTimeSheetForMonth(axios, selectedUserId, [
      selectedMonthAndYear[0] + 1,
      selectedMonthAndYear[1],
    ]);
    if (!timeCard) return;
    const fileName: string = `${selectedUser?.firstName || user.firstName}-${
      selectedUser?.lastName || user.lastName
    }_${selectedMonthAndYear[0] + 1}_${selectedMonthAndYear[1]}.pdf`;
    downloadFile(timeCard, fileName);
  };

  /**
   * Helper method to update a timeTracking object
   * @param event FormEvent
   * @returns void
   */
  const handleUpdateTracking = (event: FormEvent): void => {
    event.preventDefault();
    if (!trackingToEdit || !selectedUser) return;
    if (
      //+1 because months are saved as 1-12 and js provides 0-11
      checkForValidConfirmation(
        trackingToEdit.startDate.getMonth() + 1,
        trackingToEdit.startDate.getFullYear(),
        selectedUser,
      )
    ) {
      generateNotification({
        type: NotificationType.ERROR,
        value: t('general.notification.error.monthConfirmed'),
      });
      return;
    }
    const promise: Promise<TimeTracking | undefined> = trackingToEdit.id
      ? updateTimeTracking(axios, trackingToEdit)
      : createNewTimeTracking(axios, trackingToEdit);

    promise.then((updatedTracking) => {
      if (!updatedTracking) return;
      setTrackingToEdit(undefined);
      mutate();
    });
  };

  /**
   * Helper method to delete a timeTracking
   * @param trackingId id of the tracking to delete
   */
  const handleDeleteTimeTracking = (trackingId: string): void => {
    deleteTimeTracking(axios, trackingId).then((success) => {
      if (success) {
        generateNotification({
          type: NotificationType.SUCCESS,
          value: t('general.notification.success.deleteTimeTracking'),
        });
        setTrackingToDelete(undefined);
        mutate();
      }
    });
  };

  return isUserAllowedToDo(user.right, Right.TIME_CREATE) ? (
    <>
      <TopBar
        onBackClick={() => navigate(-1)}
        title={t('pages.timekeeping.headline', {
          replace: {
            username: getUserName(selectedUser ? selectedUser : user),
          },
        })}
      >
        <div className="topbar__content hide-on-small">
          {isUserAllowedToDo(user.right, Right.TIME_EXPORT) && (
            <IconComponent
              type={IconType.DOWNLOAD}
              onClick={handleDownloadTimeSheet}
              className="clickable"
            />
          )}
        </div>
      </TopBar>
      {newConfirmation && selectedUser && (
        <Popup
          isOpen={showConfirmationPopup}
          onClose={() => toggleShowConfirmationPopup(false)}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => toggleShowConfirmationPopup(false)}
            />,
            <Button value={t('general.buttons.save')} onClick={handleAddConfirmation} />,
          ]}
          title={t('pages.timekeeping.confirmationTitle')}
        >
          <p>
            {t('pages.timekeeping.confirmationDate', {
              replace: {
                date: newConfirmation.confirmationDate.toLocaleDateString('DE-de'),
              },
            })}
          </p>
          <p>
            {t('pages.timekeeping.confirmedBy', {
              replace: { name: getUserName(user) },
            })}
          </p>
          <TextArea
            label={t('pages.timekeeping.comment')}
            onChange={(comment) => setNewConfirmation({ ...newConfirmation, comment })}
          />
        </Popup>
      )}
      <Popup
        isOpen={showInvalidationPopup}
        title={t('pages.timekeeping.invalidationTitle')}
        onClose={() => toggleShowInvalidationPopup(false)}
        buttons={[
          <Button
            value={t('general.buttons.cancel')}
            onClick={() => toggleShowInvalidationPopup(false)}
          />,
          <Button value={t('general.buttons.save')} onClick={() => handleAddConfirmation(true)} />,
        ]}
      >
        <p>{t('pages.timekeeping.confirmInvalidation')}</p>
      </Popup>
      {trackingToEdit && (
        <Popup
          title={t('pages.timekeeping.editTrackingPopup')}
          isOpen={!!trackingToEdit}
          onClose={() => setTrackingToEdit(undefined)}
        >
          <form onSubmit={handleUpdateTracking}>
            <Input
              onChangeDate={(startDate) =>
                trackingToEdit &&
                !trackingToEdit?.id &&
                startDate &&
                setTrackingToEdit({ ...trackingToEdit, startDate })
              }
              type="date"
              value={trackingToEdit?.startDate || ''}
              disabled={!!trackingToEdit?.id}
              label={t('pages.timekeeping.date')}
            />
            <Input
              type="number"
              value={trackingToEdit?.breakTime || 0}
              label={t('pages.timekeeping.breakTimeMinutes')}
              onChangeNumber={(breakTime) => {
                setTrackingToEdit({
                  ...trackingToEdit,
                  breakTime,
                });
              }}
            />
            <Input
              type="time"
              label={t('pages.timekeeping.start')}
              value={trackingToEdit?.startTime}
              onChangeTime={(startTime) => setTrackingToEdit({ ...trackingToEdit!, startTime })}
            />
            <Input
              type="time"
              label={t('pages.timekeeping.end')}
              value={trackingToEdit?.endTime}
              onChangeTime={(endTime) =>
                trackingToEdit && endTime > trackingToEdit.startTime
                  ? setTrackingToEdit({ ...trackingToEdit!, endTime })
                  : setTrackingToEdit({
                      ...trackingToEdit!,
                      endTime,
                      endDate: dayjs(trackingToEdit?.startDate).add(1, 'day').toDate(),
                    })
              }
            />
            <Input
              type="number"
              value={trackingToEdit?.drivingTimeArrival || 0}
              label={t('pages.timekeeping.drivingTimeArrival')}
              onChangeNumber={(drivingTimeArrival) => {
                setTrackingToEdit({
                  ...trackingToEdit,
                  drivingTimeArrival,
                });
              }}
            />
            <Input
              type="number"
              value={trackingToEdit?.drivingTimeDeparture || 0}
              label={t('pages.timekeeping.drivingTimeDeparture')}
              onChangeNumber={(drivingTimeDeparture) => {
                setTrackingToEdit({
                  ...trackingToEdit,
                  drivingTimeDeparture,
                });
              }}
            />
            <Input
              type="time"
              label={t('pages.timekeeping.trackedTime')}
              value={trackingToEdit?.totalWorkTime}
              disabled
              onChangeTime={() => {}}
            />
            <Input
              type="text"
              label={t('pages.timekeeping.comment')}
              value={trackingToEdit?.comment}
              onChange={(comment) =>
                trackingToEdit && setTrackingToEdit({ ...trackingToEdit, comment })
              }
            />
            <Button type="submit" value={t('general.buttons.save')} />
            <Button
              backgroundColor="#BC2E46"
              type="button"
              value={t('general.buttons.cancel')}
              onClick={() => setTrackingToEdit(undefined)}
            />
          </form>
        </Popup>
      )}
      <div className="timetracking__container">
        <Popup
          isOpen={showWarningPopup}
          onClose={() => toggleShowWarningPopup(false)}
          title={t('pages.timekeeping.breakTimePopup')}
          buttons={[
            <Button
              value={t('general.buttons.yes')}
              onClick={() => {
                if (!activeTracking) return;
                toggleShowWarningPopup(false);
                handleEndTracking(activeTracking);
              }}
            />,
            <Button
              value={t('general.buttons.no')}
              onClick={() => toggleShowWarningPopup(false)}
            />,
          ]}
        >
          <p>{t('pages.timekeeping.popup')}</p>
        </Popup>
        {trackingToDelete && (
          <Popup
            isOpen={!!trackingToDelete}
            buttons={[
              <Button
                value={t('general.buttons.cancel')}
                onClick={() => setTrackingToDelete(undefined)}
              />,
              <Button
                value={t('general.buttons.delete')}
                type="button"
                onClick={() => handleDeleteTimeTracking(trackingToDelete)}
              />,
            ]}
          >
            <p>{t('pages.timekeeping.deletePopupText')}</p>
          </Popup>
        )}
        {user.id === selectedUserId && (
          <Box>
            <div className="worktime-display__container">
              <p className="worktime-display__headline">{t('pages.timekeeping.today')}</p>
              <div className="worktime-display__content-wrapper">
                <div className="worktime-display__worktime">
                  {activeTracking && activeTracking.endTime === 0 && (
                    <IconComponent
                      type={IconType.LOADING}
                      width={20}
                      className="worktime-display__icon"
                    />
                  )}
                  <p className="worktime-display__time">{getCorrectDisplayText(activeTracking)}</p>
                </div>
                <div className="worktime-display__input-wrapper">
                  <p className="worktime-display__input-wrapper__break">
                    {t('pages.timekeeping.break')}:
                  </p>
                  <Input
                    disabled={
                      !activeTracking || (!!activeTracking && activeTracking?.endTime !== 0)
                    }
                    maxWidth={100}
                    value={activeTracking?.breakTime || 0}
                    type="number"
                    onChangeNumber={(breakTime) => {
                      if (!activeTracking) return;
                      if (breakTime)
                        setActiveTracking({
                          ...activeTracking,
                          breakTime,
                        });
                    }}
                  />
                </div>
                <div className="worktime-display__input-wrapper">
                  <p className="worktime-display__input-wrapper__comment">
                    {t('pages.timekeeping.comment')}:
                  </p>
                  <Input
                    maxWidth={200}
                    type="text"
                    value={activeTracking?.comment}
                    onChange={(comment) =>
                      activeTracking && setActiveTracking({ ...activeTracking, comment })
                    }
                  />
                </div>
              </div>
              {activeTracking && activeTracking.endTime === 0 && (
                <Button
                  maxWidth={300}
                  backgroundColor="#BC2E46"
                  value={t('general.buttons.stop')}
                  onClick={() => {
                    handleStopTracking();
                  }}
                />
              )}
              {activeTracking && activeTracking.endTime !== 0 && (
                <Button
                  maxWidth={300}
                  disabled
                  backgroundColor="#545454"
                  value={t('general.buttons.done')}
                />
              )}
              {!activeTracking && (
                <Button
                  maxWidth={300}
                  value={t('general.buttons.start')}
                  onClick={() => {
                    handleStartTracking();
                  }}
                />
              )}
            </div>
          </Box>
        )}
        <Box>
          {timeTrackings && (
            <>
              <GenerateMonthlyOverviewBoxes
                selectedMonthAndYear={selectedMonthAndYear}
                setSelectedMonthAndYear={setSelectedMonthAndYear}
                monthlyTimes={getTotalWorkingTimeForMonths(timeTrackings)}
              />
              {confirmationButtons}
            </>
          )}
        </Box>
        {timeTrackings && (
          <Box
            title={
              <div className="timetracking-overview__box-title-wrapper">
                <p className="timetracking-overview__table-headline">
                  {t(`general.month.${selectedMonthAndYear[0]}`)}
                </p>
                <IconComponent
                  type={IconType.ADD}
                  className={`timetracking-overview__plus-icon ${selectedMonthIsValid ? 'disabled' : ''}`}
                  onClick={() =>
                    !selectedMonthIsValid &&
                    setTrackingToEdit(generateEmptyTimeTracking(selectedUserId))
                  }
                />
              </div>
            }
          >
            <div className="timetracking-overview__table-wrapper">
              <Table
                expandable
                rows={rows}
                header={
                  t('pages.timekeeping.tableHeader', {
                    returnObjects: true,
                  }) as TableHeader[]
                }
              />
            </div>
          </Box>
        )}
      </div>
    </>
  ) : (
    <NotAllowed neccessaryRight={Right.TIME_CREATE} />
  );
};
export default TimeKeepingPage;
